import { z } from 'zod';

import {
  cashTransferTypeValues,
  type CashTransferDto,
  type CashTransferType,
  type CreateCashTransferRequest,
  type CreateCashTransferResponse,
  type ListCashTransfersRequest,
  type ListCashTransfersResponse,
} from './cash-transfers.dto.js';
import { CASH_DENOMINATION_ALLOCATION_TOTAL_MISMATCH } from './cash-error-codes.js';
import { CashDenominationRowsSchema } from './cash-denominations.schema.js';

const timestampSchema = z.iso.datetime();

export const CashTransferTypeSchema: z.ZodType<CashTransferType> = z.enum(cashTransferTypeValues);

export const CashTransferDtoSchema: z.ZodType<CashTransferDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  storeId: z.uuid().nullable(),
  locationId: z.uuid().nullable(),
  stationId: z.uuid().nullable(),
  fromCashLocationId: z.uuid(),
  toCashLocationId: z.uuid(),
  amount: z.number().int().positive(),
  type: CashTransferTypeSchema,
  notes: z.string().trim().min(1).max(500).nullable(),
  createdByUserId: z.uuid(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const CreateCashTransferRequestSchema: z.ZodType<CreateCashTransferRequest> = z
  .object({
    companyId: z.uuid(),
    type: CashTransferTypeSchema,
    fromCashLocationId: z.uuid(),
    toCashLocationId: z.uuid(),
    stationId: z.uuid().optional(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    amount: z.number().int().positive(),
    notes: z.string().trim().min(1).max(500).nullable().optional(),
    denominations: CashDenominationRowsSchema.nullable().optional(),
  })
  .superRefine((request, context) => {
    if (!request.denominations) {
      return;
    }

    const denominationTotal = request.denominations.reduce(
      (total, row) => total + row.denom * row.count,
      0,
    );
    if (denominationTotal !== request.amount) {
      context.addIssue({
        code: 'custom',
        path: ['denominations'],
        message: 'Denomination total does not match the transfer amount.',
        params: { apiErrorCode: CASH_DENOMINATION_ALLOCATION_TOTAL_MISMATCH },
      });
    }
  });

export const CreateCashTransferResponseSchema: z.ZodType<CreateCashTransferResponse> = z.object({
  cashTransfer: CashTransferDtoSchema,
  warnings: z.array(z.string().trim().min(1).max(200)).optional(),
});

export const ListCashTransfersRequestSchema: z.ZodType<ListCashTransfersRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid().optional(),
  locationId: z.uuid().optional(),
  stationId: z.uuid().optional(),
  cashLocationId: z.uuid().optional(),
  type: CashTransferTypeSchema.optional(),
  from: timestampSchema.optional(),
  to: timestampSchema.optional(),
});

export const ListCashTransfersResponseSchema: z.ZodType<ListCashTransfersResponse> = z.object({
  items: z.array(CashTransferDtoSchema),
});
